import { Grid, Box, makeStyles, Skeleton, LottiePlayerECOMMBX, Container } from '@dxb-web-ui-framework/design-system';
import clsx from 'clsx';
import React, { VFC, useEffect } from 'react';
import { DataServiceHookResponse } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { formatAmount, isEmpty } from '@dxb-web-ui-framework/common/utils';
import { useTranslation } from 'react-i18next';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { isDefined } from '@dxb-web-ui-framework/common/utils/Helper';
import { SkeletonTypography } from '../../../../content-composer/src/components/Generic/Skeleton';
import { useLanguage } from '../../../../internationalization/src/contexts/Internationalization/hooks';
import { ErrorContent } from '../../../../content-composer/src/components/Partials/ErrorContent';
import { Mandate } from '../../domain/Authorization/entities';
import { useWizardStepData } from '../../../../content-composer/src/contexts/ClientSideWizard/hooks';
import {
  useAuthorizationTransactionDetails,
  useOnRefetchPendingAuthizationsListMutation,
  useAuthorizationLoadingState,
  useAuthorizationErrorState,
  useOnTransactionDetailsMutation,
  useBulkSelectedAuthorizations,
  useIsAuthFlowDrawerOpen,
  useAuthorizationSelectedAuthorization,
} from '../../contexts';
import { ExecuteMandateResponseBody } from '../../domain/Authorization/entities/ExecuteMandateResponseDTO';
import { useMandateService } from './useMandateService';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
  },
  container: {
    gap: theme.spacing(2),
  },
  successText: {
    color: theme.palette.success.main,
  },
  text: {
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
  rejected: {
    display: 'inline-block',
    color: theme.palette.error.main,
  },
  lottieContainer: {
    maxHeight: '520px',
    width: '100%',
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      margin: 'auto',
    },
  },
}));

export interface MandateExecuteStepProps {
  authAction: string | null;
  isBulk?: boolean;
}

const mandatesGlobalIdGetter = (mandates: Mandate[] | []) => {
  if (isEmpty(mandates)) {
    return '';
  }

  let mandatesGlobalOrderIds: string = '';

  mandates.forEach(m => {
    if (m) {
      if (!mandatesGlobalOrderIds) {
        mandatesGlobalOrderIds = mandatesGlobalOrderIds + `${m.globalOrderId}`;
      } else {
        mandatesGlobalOrderIds = mandatesGlobalOrderIds + `, ${m.globalOrderId}`;
      }
    }
  });
  return mandatesGlobalOrderIds;
};

const MandateExecuteStep: VFC<MandateExecuteStepProps> = ({ authAction, isBulk = false }) => {
  const { t } = useTranslation();
  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const language = useLanguage();
  const bulkSelectedAuthorizations = useBulkSelectedAuthorizations();
  const previousStepData = useWizardStepData('mandateInitiate') as {
    values: { requestId?: string };
  };

  // drawer state
  const isAuthFlowDrawerOpen = useIsAuthFlowDrawerOpen();
  // loading state
  const isLoadingTransactionDetails = useAuthorizationLoadingState('isLoadingTransactionDetails');
  const isLoadingCheckAuthorization = useAuthorizationLoadingState('isLoadingCheckAuthorization');

  // error state
  const transactionDetailsError = useAuthorizationErrorState('transactionDetailsError');

  // mutations
  const refetchTransactionDetailsMutation = useOnTransactionDetailsMutation();
  const refetchMandatesListMutation = useOnRefetchPendingAuthizationsListMutation();

  // provider data
  const selectedAuthorizationTransactionDetails = useAuthorizationTransactionDetails();
  const selectedAuthorization = useAuthorizationSelectedAuthorization();

  // Execute Authorization
  const {
    data: executeMandateData,
    isLoading: isLoadingExecuteAuthorization,
    error: executeAuthorizationError,
    mutate: onExecuteAuthorizationMutation,
  } = (
    useMandateService(isBulk, 'execute') as (
      successCallback: () => void,
      requestId?: Nullable<string>,
    ) => DataServiceHookResponse<ExecuteMandateResponseBody>
  )(() => {}, previousStepData.values?.requestId);

  const globalOrderId = selectedAuthorization?.globalOrderId;
  const globalOrderIds = mandatesGlobalIdGetter(bulkSelectedAuthorizations);
  const isLoading = isLoadingTransactionDetails || isLoadingCheckAuthorization || isLoadingExecuteAuthorization;
  const error = transactionDetailsError || executeAuthorizationError;

  useEffect(() => {
    if (executeMandateData) {
      refetchMandatesListMutation();
    }
  }, [isAuthFlowDrawerOpen, refetchMandatesListMutation, executeMandateData]);

  const mutatorHandler = () => {
    if (transactionDetailsError) {
      refetchTransactionDetailsMutation();
      return;
    }

    onExecuteAuthorizationMutation && onExecuteAuthorizationMutation();
  };

  const successTitleRenderer = () => {
    if (executeMandateData?.transferExecuted === true) {
      if (globalOrderId && !globalOrderIds) {
        return `${t('authorizationsListPage.ApproveRejectFlow.isLastAuthorizer.Success.Subtitle')} ${globalOrderId}`;
      } else {
        return `${t(
          'authorizationsListPage.ApproveRejectFlow.isLastAuthorizer.Success.Bulk.Subtitle',
        )} ${globalOrderIds}`;
      }
    } else {
      if (globalOrderId && !globalOrderIds) {
        return t('authorizationsListPage.ApproveRejectFlow.isNotLastAuthorizer.Success.Subtitle');
      } else {
        return `${t(
          'authorizationsListPage.ApproveRejectFlow.isNotLastAuthorizer.Success.Bulk.Subtitle',
        )} ${globalOrderIds}`;
      }
    }
  };

  const classes = useStyles();

  return (
    <Box display='flex' flexDirection='column' width='100%' height='100%' alignItems='center' justifyContent='center'>
      {error && !isLoading && (
        <ErrorContent
          code={error?.code || '404'}
          onClickBtn={mutatorHandler}
          subtitle={error?.message || t('authorizationsListPage.ApproveRejectFlow.ErrorContent.Execute.Subtitle')}
          btnLabel={t('authorizationsListPage.ApproveRejectFlow.ErrorContent.BtnLabel')}
          message={t('authorizationsListPage.ApproveRejectFlow.ErrorContent.Message')}
        />
      )}
      {!error && isEmpty(error) && (
        <Container maxWidth='md' className={classes.root}>
          <Grid container className={classes.container} justifyContent='center'>
            <Grid container item xs={12} md={3} justifyContent='center' direction='column'>
              <SkeletonTypography
                hasSkeleton={isLoading}
                skeletonProps={{ variant: 'text', width: '100%' }}
                typographyProps={{
                  variant: isMobile ? 'h3' : 'h2',
                  component: 'h1',
                  className: clsx(classes.successText, classes.text),
                }}>
                {t('fundsTransfer.SuccessStep.label')}
              </SkeletonTypography>
              <Box mt={2} mb={2}>
                {authAction === 'approve' && (
                  <SkeletonTypography
                    skeletonProps={{ variant: 'text', width: '100%' }}
                    hasSkeleton={isLoading}
                    typographyProps={{ variant: 'h5', color: 'secondary', className: classes.text }}>
                    {successTitleRenderer()}
                  </SkeletonTypography>
                )}
                {authAction === 'reject' && (
                  <SkeletonTypography
                    skeletonProps={{ variant: 'text', width: '100%' }}
                    hasSkeleton={isLoading}
                    typographyProps={{ variant: 'h5', color: 'secondary', className: classes.text }}>
                    {t('authorizationsListPage.ApproveRejectFlow.Success.Reject.Subtitle')}{' '}
                    <span className={classes.rejected}>
                      {t('authorizationsListPage.ApproveRejectFlow.Success.Reject.Subtitle2')}
                    </span>{' '}
                    {globalOrderIds &&
                      `${t(
                        'authorizationsListPage.ApproveRejectFlow.Success.Reject.Bulk.Subtitle3',
                      )} ${globalOrderIds}`}
                    {isDefined(globalOrderId) &&
                      `${t('authorizationsListPage.ApproveRejectFlow.Success.Reject.Subtitle3')} ${globalOrderId}`}
                  </SkeletonTypography>
                )}
              </Box>
              {isDefined(globalOrderId) && (
                <SkeletonTypography
                  skeletonProps={{ variant: 'text', width: '100%' }}
                  hasSkeleton={isLoading}
                  typographyProps={{ color: 'textSecondary', className: classes.text }}>
                  {selectedAuthorizationTransactionDetails?.amount &&
                  selectedAuthorizationTransactionDetails?.exchangeCurrency
                    ? formatAmount(
                        Number(selectedAuthorizationTransactionDetails?.amount),
                        selectedAuthorizationTransactionDetails?.exchangeCurrency,
                        2,
                        language.cultureName,
                      )
                    : '-'}
                </SkeletonTypography>
              )}
            </Grid>
            <Grid item xs={12} md={8}>
              <Box className={classes.lottieContainer} mt={isLoading ? 2 : 0} flex={1}>
                {isLoading ? (
                  <Skeleton width='100%' height='100%'>
                    <LottiePlayerECOMMBX icon='ecommbx_success' loop play />
                  </Skeleton>
                ) : (
                  <LottiePlayerECOMMBX icon='ecommbx_success' loop play />
                )}
              </Box>
            </Grid>
          </Grid>
        </Container>
      )}
    </Box>
  );
};

export { MandateExecuteStep };
